// Gates a route on the single session. `admin` additionally requires a
// platform owner/admin group. This is UX/defense-in-depth only — the real
// authorization is the IAM role behind the user's STS session.
//
// Signed out, we go STRAIGHT to the hosted UI. There used to be a "Sign in to
// continue" card with a Sign in button, which is a click that asks the user to
// confirm a decision they already made by opening the console. The only thing
// that page ever did was call signIn().
//
// ...but not blindly. If the hosted UI bounces the user back without a usable
// session (cancelled login, a callback that fails to exchange), redirecting again
// on sight lands them in an infinite loop with no way to read the error. So we
// auto-redirect ONCE per tab per destination; after that, or on any sign-in
// error, we fall back to the manual card — which is now a real escape hatch
// rather than a toll booth.

import { Alert, Button, Center, Loader, Paper, Stack, Text, Title } from "@mantine/core";
import { IconLock, IconShieldX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../session/SessionProvider.tsx";

const GUARD_KEY = "s3p:auto-signin-attempted";

/** One automatic attempt per tab. If sign-in bounces the user back without a
 *  session, redirecting again on sight is an infinite loop they can't read the
 *  error through — so the second time we show the manual card instead. */
function claimAttempt(): boolean {
  try {
    if (sessionStorage.getItem(GUARD_KEY)) return false;
    sessionStorage.setItem(GUARD_KEY, "1");
    return true;
  } catch {
    // Storage disabled: we can't detect a loop, so don't start one.
    return false;
  }
}

/** Signing in successfully proves the redirect works, so re-arm it. Without
 *  this, the guard was a ONE-WAY latch: sign in, then sign out in the same tab,
 *  and the auto-redirect refused to fire — while the render still showed
 *  "Taking you to sign in…", because it keyed the spinner off "should redirect"
 *  rather than "did redirect". That's a permanent spinner with no way out. */
function releaseAttempt(): void {
  try {
    sessionStorage.removeItem(GUARD_KEY);
  } catch {
    /* nothing to release */
  }
}

export function RequireSession({
  admin = false,
  returnTo,
  children,
}: {
  admin?: boolean;
  returnTo: string;
  children: React.ReactNode;
}) {
  const { ready, signedIn, isAdmin, error, signIn } = useSession();
  // Did WE actually start a redirect? The spinner must key off this, never off
  // "we ought to redirect" — otherwise any path that declines to redirect spins
  // forever.
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (signedIn) {
      releaseAttempt(); // the flow works — let a later sign-out redirect again
      return;
    }
    if (error) return; // show them the error, don't bounce
    if (!claimAttempt()) return; // already tried this tab → fall through to the card
    setRedirecting(true);
    signIn(returnTo);
  }, [ready, signedIn, error, returnTo, signIn]);

  // Resolving the session, or actually on our way out — one spinner for both,
  // because to the user they're the same moment.
  if (!ready || redirecting) {
    return (
      <Center mih={320} p="md">
        <Stack align="center" gap="sm">
          <Loader />
          {redirecting && (
            <Text c="dimmed" size="sm">
              Taking you to sign in…
            </Text>
          )}
        </Stack>
      </Center>
    );
  }

  // Reached only when the automatic attempt already happened (or failed): give
  // the user the error and a way to try again by hand.
  if (!signedIn) {
    return (
      <Center mih={320} p="md">
        <Paper withBorder p="xl" radius="md" maw={460}>
          <Stack gap="md">
            <IconLock size={28} />
            <Title order={3}>Sign in to continue</Title>
            <Text c="dimmed" size="sm">
              Signs in via the platform's Cognito hosted UI. The Identity Pool maps your groups to
              an IAM role and the SDK gets scoped credentials back.
            </Text>
            {error && (
              <Alert color="red" variant="light" title="Sign-in error">
                {error}
              </Alert>
            )}
            <Button onClick={() => signIn(returnTo)}>Sign in</Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  if (admin && !isAdmin) {
    return (
      <Center mih={320} p="md">
        <Paper withBorder p="xl" radius="md" maw={460}>
          <Stack gap="md">
            <IconShieldX size={28} color="var(--mantine-color-yellow-5)" />
            <Title order={3}>Admins only</Title>
            <Text c="dimmed" size="sm">
              This area is for platform owners and admins. Your account doesn't have an admin role —
              ask an owner to add you to the right group.
            </Text>
            <Button component={Link} to="/" variant="default">
              Back to your apps
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  return <>{children}</>;
}
